import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  Breadcrumbs,
  Chip,
  TextField,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import { validateItemName } from '../../utils/formatUtils';
import { formatPath } from '../../utils/formatUtils';

const PathSelectorDialog = ({ open, onClose, onSelect, currentPath = '/', title = "Select Folder" }) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [browsePath, setBrowsePath] = useState('/');
  const [selectedPath, setSelectedPath] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Home', path: '/' }]);
  const [error, setError] = useState(null);
  
  // New folder creation states
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderError, setNewFolderError] = useState(null);
  const [createFolderLoading, setCreateFolderLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize path to current path when dialog opens
      setBrowsePath(currentPath || '/');
      setSelectedPath(null);
      loadFolders(currentPath || '/');
    }
  }, [open, currentPath]);

  const loadFolders = async (path) => {
    setError(null);
    setLoading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('authUser'));
      if (!userData) {
        setError('User data not found');
        setLoading(false);
        return;
      }

      const response = await fileSystemAccessServiceInstance.getFolderContents(
        path, 
        userData.email, 
        userData.access
      );

      // Filter to only show folders
      const folderItems = (response.data || [])
        .filter(item => item && item.isFolder === true)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      
      setFolders(folderItems);
      updateBreadcrumbs(path);
      
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Failed to load folders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBreadcrumbs = (path) => {
    const parts = path.split('/').filter(Boolean);
    const crumbs = [{ name: 'Home', path: '/' }];
    
    let currentPath = '';
    for (const part of parts) {
      currentPath += '/' + part;
      crumbs.push({
        name: part,
        path: currentPath
      });
    }
    
    setBreadcrumbs(crumbs);
  };

  const handleFolderClick = (folder) => {
    const folderPath = folder.path || `${browsePath === '/' ? '' : browsePath}/${folder.name}`;
    setBrowsePath(folderPath);
    loadFolders(folderPath);
  };

  const handleBreadcrumbClick = (path) => {
    setBrowsePath(path);
    loadFolders(path);
  };

  const handleGoBack = () => {
    if (browsePath === '/') return;
    
    const pathParts = browsePath.split('/').filter(Boolean);
    pathParts.pop();
    const parentPath = pathParts.length ? '/' + pathParts.join('/') : '/';
    
    setBrowsePath(parentPath);
    loadFolders(parentPath);
  };

  const handleSelectCurrentFolder = () => {
    setSelectedPath(browsePath);
  };

  const handleConfirm = () => {
    // If there's already a selected path, use it
    if (selectedPath) {
      onSelect(selectedPath);
      onClose();
    } 
    // Otherwise use the current browsing path
    else {
      onSelect(browsePath);
      onClose();
    }
  };

  // Handle starting folder creation
  const handleStartCreateFolder = () => {
    setIsCreatingFolder(true);
    setNewFolderName('');
    setNewFolderError(null);
    // Don't clear the selection when creating a folder
    // This way the selected path remains intact
  };
  
  // Handle canceling folder creation
  const handleCancelCreateFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName('');
    setNewFolderError(null);
  };
  
  // Handle folder name change
  const handleNewFolderNameChange = (e) => {
    const name = e.target.value;
    setNewFolderName(name);
    
    // Validate the folder name as the user types
    if (name) {
      const error = validateItemName(name, 'folder');
      setNewFolderError(error);
    } else {
      setNewFolderError(null);
    }
  };
  
  // Handle folder creation submit
  const handleCreateFolderSubmit = async () => {
    if (!newFolderName) {
      setNewFolderError('Folder name is required');
      return;
    }
    
    const error = validateItemName(newFolderName, 'folder');
    if (error) {
      setNewFolderError(error);
      return;
    }
    
    setCreateFolderLoading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('authUser'));
      
      if (!userData) {
        setNewFolderError('User data not found');
        setCreateFolderLoading(false);
        return;
      }
      
      await fileSystemAccessServiceInstance.createFolder(
        browsePath, 
        newFolderName, 
        userData.email, 
        userData.access
      );
      
      // Clear states and reload the folder list
      setIsCreatingFolder(false);
      setNewFolderName('');
      setNewFolderError(null);
      
      // Reload folders to show the newly created folder
      await loadFolders(browsePath);
      
    } catch (err) {
      console.error('Error creating folder:', err);
      setNewFolderError(err.response?.data?.message || 'Failed to create folder. Please try again.');
    } finally {
      setCreateFolderLoading(false);
    }
  };
  
  // Handle key down in the new folder input
  const handleNewFolderKeyDown = (e) => {
    if (e.key === 'Enter' && !newFolderError && newFolderName) {
      handleCreateFolderSubmit();
    } else if (e.key === 'Escape') {
      handleCancelCreateFolder();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          height: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">{title}</Typography>
        <Tooltip title="Create New Folder">
          <IconButton 
            color="primary" 
            onClick={handleStartCreateFolder}
            size="small"
            sx={{ ml: 1 }}
            disabled={isCreatingFolder || loading}
          >
            <CreateNewFolderIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      
      <Box sx={{ px: 3, py: 1 }}>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="folder navigation"
          sx={{ mb: 1, flexWrap: 'wrap' }}
        >
          {breadcrumbs.map((crumb, index) => (
            <Button
              key={crumb.path}
              color={browsePath === crumb.path ? "primary" : "inherit"}
              variant={browsePath === crumb.path ? "text" : "text"}
              onClick={() => handleBreadcrumbClick(crumb.path)}
              size="small"
              startIcon={index === 0 ? <HomeIcon fontSize="small" /> : null}
              sx={{ 
                textTransform: 'none',
                fontWeight: browsePath === crumb.path ? 600 : 400
              }}
            >
              {crumb.name}
            </Button>
          ))}
        </Breadcrumbs>
        
        
        {/* New folder creation form */}
        {isCreatingFolder && (
          <Paper 
            variant="outlined" 
            sx={{ 
              mt: 2, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              borderColor: 'primary.light',
              bgcolor: 'background.paper'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <CreateNewFolderIcon fontSize="small" sx={{ mr: 1 }} />
              Create Folder in {browsePath === '/' ? 'Home' : formatPath(browsePath)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                label="Folder Name"
                value={newFolderName}
                onChange={handleNewFolderNameChange}
                onKeyDown={handleNewFolderKeyDown}
                error={!!newFolderError}
                helperText={newFolderError}
                size="small"
                variant="outlined"
                placeholder="Enter folder name"
                disabled={createFolderLoading}
              />
              
              <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleCreateFolderSubmit}
                  disabled={!newFolderName || !!newFolderError || createFolderLoading}
                  startIcon={createFolderLoading ? <CircularProgress size={14} /> : <AddIcon />}
                  sx={{ minWidth: '100px', mb: 0.5 }}
                >
                  {createFolderLoading ? 'Creating...' : 'Create'}
                </Button>
                
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelCreateFolder}
                  disabled={createFolderLoading}
                  startIcon={<CloseIcon />}
                  sx={{ minWidth: '100px' }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
      
      <Divider />
      
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button 
              variant="outlined" 
              onClick={() => loadFolders(browsePath)} 
              sx={{ mt: 1 }}
            >
              Try Again
            </Button>
          </Box>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ py: 0 }}>
            {/* Back button for non-root folders */}
            {browsePath !== '/' && (
              <ListItemButton onClick={handleGoBack} divider>
                <ListItemIcon>
                  <ArrowBackIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary="Go back to parent folder" 
                  //secondary={breadcrumbs[breadcrumbs.length - 2]?.path || '/'}
                />
              </ListItemButton>
            )}

            
            
            {/* Folder list */}
            {folders.length === 0 ? (
              !isCreatingFolder && (
                <Box sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  borderRadius: 1
                }}>
                  <Typography color="text.secondary">
                    No subfolders found in this location
                  </Typography>
                </Box>
              )
            ) : (
              <List>
                {folders.map((folder) => (
                  <ListItemButton 
                    key={folder.path || folder.name} 
                    onClick={() => handleFolderClick(folder)}
                    divider
                  >
                    <ListItemIcon>
                      <FolderIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={folder.name} 
                      //secondary={folder.path || `${browsePath === '/' ? '/' : browsePath + '/'}${folder.name}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
        >
          Cancel
        </Button>
        
        <Box>
          {!isCreatingFolder && (
            <Button 
              variant="outlined"
              color="primary"
              onClick={handleStartCreateFolder}
              startIcon={<CreateNewFolderIcon />}
              sx={{ mr: 1 }}
              disabled={loading || createFolderLoading}
            >
              New Folder
            </Button>
          )}
          
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            color="primary"
            disabled={isCreatingFolder}
            startIcon={<FolderIcon />}
          >
            {selectedPath ? 'Select Folder' : 'Use Current Folder'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PathSelectorDialog;